import { useState } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import { Plus } from 'lucide-react';
import { useAdminLocations, useAdminUsers } from '@/lib/queries';
import { useCreateLocation, useCreateUser, useDeactivateUser } from '@/lib/admin';
import { AdminLayout } from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { StatusBadge } from '@/components/ui/badge';

// Locations & User Management — two tabs: locations table, users table with
// invite flow.
export function LocationsUsers() {
  return (
    <AdminLayout>
      <h1 className="page-title">Locations &amp; Users</h1>
      <p className="page-sub mb-6">Manage restaurant locations and chef accounts.</p>
      <Tabs.Root defaultValue="locations">
        <Tabs.List className="mb-6 flex gap-6 border-b border-line">
          <Tab value="locations" label="Locations" />
          <Tab value="users" label="Users" />
        </Tabs.List>
        <Tabs.Content value="locations">
          <LocationsTab />
        </Tabs.Content>
        <Tabs.Content value="users">
          <UsersTab />
        </Tabs.Content>
      </Tabs.Root>
    </AdminLayout>
  );
}

function Tab({ value, label }: { value: string; label: string }) {
  return (
    <Tabs.Trigger
      value={value}
      className="border-b-2 border-transparent px-1 pb-2 text-sm text-muted data-[state=active]:border-brand data-[state=active]:text-brand"
    >
      {label}
    </Tabs.Trigger>
  );
}

function LocationsTab() {
  const { data } = useAdminLocations();
  const createLocation = useCreateLocation();
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');

  const locations = data?.data ?? [];

  async function addLocation() {
    if (!name) return;
    await createLocation.mutateAsync({ name, address: address || undefined });
    setName('');
    setAddress('');
  }

  return (
    <div>
      <div className="mb-4 flex max-w-xl gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Location name" />
        <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Address" />
        <Button onClick={addLocation} disabled={!name} className="shrink-0">
          <Plus className="h-4 w-4" /> Add
        </Button>
      </div>
      <Card className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-line text-muted">
              <th className="px-4 py-3 font-medium">Location</th>
              <th className="px-4 py-3 font-medium">Address</th>
              <th className="px-4 py-3 font-medium">Active Chefs</th>
              <th className="px-4 py-3 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {locations.map((l) => (
              <tr key={l.id} className="border-b border-line">
                <td className="px-4 py-3 text-ink">{l.name}</td>
                <td className="px-4 py-3 text-muted">{l.address ?? '—'}</td>
                <td className="px-4 py-3 text-ink">{l.chefCount}</td>
                <td className="px-4 py-3">
                  <StatusBadge tone={l.isActive ? 'green' : 'ghost'}>{l.isActive ? 'Active' : 'Inactive'}</StatusBadge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function UsersTab() {
  const { data } = useAdminUsers();
  const { data: locations } = useAdminLocations();
  const createUser = useCreateUser();
  const deactivateUser = useDeactivateUser();
  const [showInvite, setShowInvite] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);

  const users = data?.data ?? [];

  function toggleLocation(id: string) {
    setSelectedLocations((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  async function invite() {
    if (!email) return;
    await createUser.mutateAsync({ email, name: name || email.split('@')[0], role: 'chef', locationIds: selectedLocations });
    setShowInvite(false);
    setName('');
    setEmail('');
    setSelectedLocations([]);
  }

  return (
    <div>
      <div className="mb-4 flex justify-between">
        <span />
        <Button onClick={() => setShowInvite((v) => !v)}>
          <Plus className="h-4 w-4" /> Invite User
        </Button>
      </div>

      {showInvite && (
        <Card className="mb-6 p-6">
          <h3 className="mb-4 font-head text-lg font-extrabold uppercase text-ink">Invite User</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" />
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" type="email" />
          </div>
          <div className="mt-4">
            <span className="mb-2 block text-sm text-muted">Location assignment</span>
            <div className="flex flex-wrap gap-2">
              {(locations?.data ?? []).map((l) => (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => toggleLocation(l.id)}
                  className={
                    selectedLocations.includes(l.id)
                      ? 'rounded-full bg-brand px-3 py-1 text-sm text-[#04181d]'
                      : 'rounded-full bg-surface-2 px-3 py-1 text-sm text-muted hover:text-ink'
                  }
                >
                  {l.name}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-6 flex justify-end">
            <Button onClick={invite} disabled={!email}>
              Send Invite
            </Button>
          </div>
        </Card>
      )}

      <Card className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-line text-muted">
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 font-medium">Role</th>
              <th className="px-4 py-3 font-medium">Assigned Locations</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-line">
                <td className="px-4 py-3 text-ink">{u.name ?? '—'}</td>
                <td className="px-4 py-3 text-ink">{u.email}</td>
                <td className="px-4 py-3 text-muted">{u.role === 'super_admin' ? 'Super Admin' : 'Chef'}</td>
                <td className="px-4 py-3 text-muted">{u.locations.map((l) => l.name).join(', ') || '—'}</td>
                <td className="px-4 py-3">
                  <StatusBadge tone={u.isActive ? 'green' : 'ghost'}>{u.isActive ? 'Active' : 'Inactive'}</StatusBadge>
                </td>
                <td className="px-4 py-3">
                  {u.role !== 'super_admin' && u.isActive && (
                    <button
                      className="text-sm text-muted hover:text-danger"
                      onClick={() => void deactivateUser.mutateAsync(u.id)}
                    >
                      Deactivate
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
